import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import PersonIcon from '@material-ui/icons/Person';
import GroupIcon from '@material-ui/icons/Group';
import WarningIcon from '@material-ui/icons/Warning';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { spassuCodeApiRef } from '../../api';

interface EntityOption {
  entityRef: string;
  displayName: string;
  kind: 'User' | 'Group';
}

const DIALOG_TITLE_ID = 'add-global-admin-dialog-title';
const DIALOG_DESC_ID = 'add-global-admin-dialog-description';
const MAX_AUTOCOMPLETE_RESULTS = 50;

const useStyles = makeStyles(theme => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    minWidth: 400,
    marginTop: theme.spacing(1),
  },
  warning: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.contrastText,
    borderRadius: theme.shape.borderRadius,
    fontSize: '0.875rem',
  },
  warningIcon: {
    color: theme.palette.warning.dark,
    flexShrink: 0,
    marginTop: 2,
  },
  error: {
    color: theme.palette.error.main,
  },
  optionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  optionIcon: {
    color: theme.palette.text.secondary,
    fontSize: 20,
  },
  optionText: {
    display: 'flex',
    flexDirection: 'column',
  },
  optionRef: {
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
  },
}));

/**
 * Build a local end-of-day ISO timestamp from a YYYY-MM-DD string.
 * Ensures the expiration honors the user's local timezone (e.g., 2026-04-10
 * end-of-day in São Paulo, not in UTC, so admin does not lose hours of access).
 */
function buildLocalEndOfDayIso(yyyymmdd: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(yyyymmdd);
  if (!match) return null;
  const [, y, m, d] = match;
  const local = new Date(Number(y), Number(m) - 1, Number(d), 23, 59, 59, 999);
  if (isNaN(local.getTime())) return null;
  return local.toISOString();
}

export interface AddGlobalAdminDialogProps {
  open: boolean;
  onClose: () => void;
  onAdded: () => void;
}

export const AddGlobalAdminDialog = ({
  open,
  onClose,
  onAdded,
}: AddGlobalAdminDialogProps) => {
  const classes = useStyles();
  const api = useApi(spassuCodeApiRef);
  const catalogApi = useApi(catalogApiRef);

  const [selected, setSelected] = useState<EntityOption | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [allEntities, setAllEntities] = useState<EntityOption[]>([]);
  const [loadingEntities, setLoadingEntities] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  const [hasExpiration, setHasExpiration] = useState(false);
  const [expiresAt, setExpiresAt] = useState('');

  // Track if component is still mounted (prevents setState after unmount)
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Reset form state when dialog opens and fetch Catalog entities ONCE
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    setSelected(null);
    setInputValue('');
    setError(null);
    setConfirmed(false);
    setHasExpiration(false);
    setExpiresAt('');

    let cancelled = false;
    setLoadingEntities(true);
    catalogApi
      .getEntities({ filter: [{ kind: 'User' }, { kind: 'Group' }] })
      .then(response => {
        if (cancelled) return;
        const entities: EntityOption[] = (response.items ?? []).map(entity => {
          const kind = entity.kind as 'User' | 'Group';
          const namespace = entity.metadata?.namespace ?? 'default';
          const name = entity.metadata?.name ?? '';
          const profile = (entity.spec as Record<string, unknown>)?.profile as
            | Record<string, unknown>
            | undefined;
          const displayName = (profile?.displayName as string) ?? name;
          return {
            entityRef: `${kind.toLowerCase()}:${namespace}/${name}`,
            displayName,
            kind,
          };
        });
        if (!mountedRef.current) return;
        setAllEntities(entities);
        setLoadingEntities(false);
      })
      .catch(() => {
        if (!mountedRef.current || cancelled) return;
        setAllEntities([]);
        setLoadingEntities(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, catalogApi]);

  // Local filtering of already-fetched entities (no network per keystroke)
  const options = useMemo<EntityOption[]>(() => {
    const q = inputValue.trim().toLowerCase();
    if (q.length < 2) return [];
    return allEntities
      .filter(
        e =>
          e.displayName.toLowerCase().includes(q) ||
          e.entityRef.toLowerCase().includes(q),
      )
      .slice(0, MAX_AUTOCOMPLETE_RESULTS);
  }, [allEntities, inputValue]);

  const clearError = useCallback(() => {
    if (error) setError(null);
  }, [error]);

  const handleSubmit = async () => {
    const entityRef = selected?.entityRef ?? inputValue.trim();
    if (!entityRef) {
      setError('Selecione um usuário ou grupo');
      return;
    }
    if (
      !selected &&
      !/^(user|group):[a-z0-9._-]+\/[a-zA-Z0-9._-]+$/.test(entityRef)
    ) {
      setError(
        'Formato inválido. Use: user:default/nome ou group:default/time',
      );
      return;
    }
    if (!confirmed) {
      setError('Você precisa confirmar a concessão de acesso global');
      return;
    }
    if (hasExpiration && !expiresAt) {
      setError('Selecione a data de expiração');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      let expiration: string | undefined;
      if (hasExpiration && expiresAt) {
        const iso = buildLocalEndOfDayIso(expiresAt);
        if (!iso) {
          setError('Data de expiração inválida');
          setSubmitting(false);
          return;
        }
        expiration = iso;
      }
      // Global admin: projectKey='*', role='admin', no repositoryKeys
      await api.addProjectMember('*', entityRef, 'admin', expiration);
      onAdded();
      onClose();
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  };

  const minDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
  }, []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby={DIALOG_TITLE_ID}
      aria-describedby={DIALOG_DESC_ID}
    >
      <DialogTitle id={DIALOG_TITLE_ID}>
        Adicionar Administrador Global
      </DialogTitle>
      <DialogContent>
        <div className={classes.form}>
          <Box id={DIALOG_DESC_ID} className={classes.warning} role="alert">
            <WarningIcon className={classes.warningIcon} fontSize="small" />
            <div>
              <strong>Atenção:</strong> administradores globais têm acesso total
              ao Spassu Code — gerenciam membros, políticas, skills e veem todos
              os projetos. Conceda com critério.
            </div>
          </Box>

          <Autocomplete
            freeSolo
            options={options}
            noOptionsText={(() => {
              if (loadingEntities) {
                return 'Carregando do Catalog...';
              }
              if (inputValue.length < 2) {
                return 'Digite ao menos 2 caracteres';
              }
              if (allEntities.length === 0) {
                return 'Catalog indisponível. Você pode digitar o entity ref manualmente.';
              }
              return 'Nenhum resultado. Você pode digitar o entity ref manualmente.';
            })()}
            getOptionLabel={opt =>
              typeof opt === 'string' ? opt : opt.displayName
            }
            renderOption={opt => (
              <div className={classes.optionRow}>
                {opt.kind === 'Group' ? (
                  <GroupIcon className={classes.optionIcon} />
                ) : (
                  <PersonIcon className={classes.optionIcon} />
                )}
                <div className={classes.optionText}>
                  <span>{opt.displayName}</span>
                  <span className={classes.optionRef}>{opt.entityRef}</span>
                </div>
              </div>
            )}
            value={selected}
            onChange={(_e, newValue) => {
              clearError();
              if (typeof newValue === 'string') {
                setSelected(null);
                setInputValue(newValue);
              } else {
                setSelected(newValue);
              }
            }}
            inputValue={inputValue}
            onInputChange={(_e, newInput) => {
              clearError();
              setInputValue(newInput);
            }}
            loading={loadingEntities}
            renderInput={params => (
              <TextField
                {...params}
                label="Usuário ou Grupo"
                placeholder="Buscar por nome..."
                helperText="Busca usuários e grupos do Backstage Catalog"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingEntities && <CircularProgress size={18} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={hasExpiration}
                onChange={() => {
                  clearError();
                  setHasExpiration(prev => !prev);
                }}
                color="primary"
              />
            }
            label="Acesso temporário (com data de expiração)"
          />
          {hasExpiration && (
            <TextField
              label="Expira em"
              type="date"
              value={expiresAt}
              onChange={e => {
                clearError();
                setExpiresAt(e.target.value);
              }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: minDate }}
              fullWidth
              helperText="O acesso de admin será removido automaticamente após esta data (23:59 do seu fuso local)"
            />
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={confirmed}
                onChange={() => {
                  clearError();
                  setConfirmed(prev => !prev);
                }}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                Confirmo que esta entidade terá <strong>acesso total</strong> ao
                Spassu Code
              </Typography>
            }
          />

          {error && (
            <Typography variant="body2" className={classes.error} role="alert">
              {error}
            </Typography>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={
            submitting || (!selected && !inputValue.trim()) || !confirmed
          }
        >
          {submitting ? 'Concedendo...' : 'Conceder acesso admin'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
